import { useReport } from '../reporting/ReportContext';
import { FilterBar } from '../reporting/FilterBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { MultiLine } from '../dashboard/charts';
import { CHANNEL_COLORS } from '../dashboard/colors';
import { downloadCsv, km, num, pct, periodDelta, shortDate } from '../lib/reporting';

const PRIMARY = CHANNEL_COLORS.NVS;

export function Difot() {
  const { summary, prevSummary, loading, error, filters } = useReport();

  const exportCsv = () => {
    if (!summary) return;
    downloadCsv(
      `nvs-difot-${filters.from}_${filters.to}.csv`,
      summary.byRegion.map((r) => ({
        region: r.region,
        stops: r.stops,
        difot: pct(r.difotRate, 2),
        delivered: pct(r.deliveredRate, 2),
        in_full: pct(r.inFullRate, 2),
        avg_distance_km: r.avgKm ?? '',
      })),
    );
  };

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / DIFOT"
        title="DIFOT"
        subtitle={
          summary
            ? `NVS in-house · ${summary.from} → ${summary.to} · Delivered In Full (on-time excluded — no SLA window). DPD handoffs counted separately.`
            : 'NVS in-house · Delivered In Full On Time'
        }
      />
      <FilterBar onExport={exportCsv} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!summary && !error && <p className="muted">Loading…</p>}

      {summary && (
        <>
          <div className="kpi-grid" aria-busy={loading}>
            <KpiCard label="DIFOT" value={pct(summary.rates.difot)} sub="Delivered & in full" tone={summary.rates.difot >= 0.95 ? 'good' : 'neutral'} accent={PRIMARY} delta={periodDelta(summary.rates.difot, prevSummary?.rates.difot, { kind: 'rate' })} />
            <KpiCard label="Delivered" value={pct(summary.rates.delivered)} sub={`${num(summary.totals.delivered)} of ${num(summary.totals.stops)} stops`} accent={PRIMARY} delta={periodDelta(summary.rates.delivered, prevSummary?.rates.delivered, { kind: 'rate' })} />
            <KpiCard label="In full" value={pct(summary.rates.inFull)} sub={`${num(summary.totals.inFull)} stops fully delivered`} accent={PRIMARY} delta={periodDelta(summary.rates.inFull, prevSummary?.rates.inFull, { kind: 'rate' })} />
            <KpiCard label="On time" value="n/a" sub="No SLA/promised window in source" tone="neutral" accent="var(--border)" />
          </div>

          <Panel title="Daily DIFOT" subtitle="Delivered-in-full rate by day">
            {summary.daily.length > 0 ? (
              <MultiLine
                categories={summary.daily.map((d) => shortDate(d.date))}
                series={[{ name: 'DIFOT', color: PRIMARY, points: summary.daily.map((d) => d.difotRate) }]}
                format={(v) => pct(v, 0)}
              />
            ) : (
              <p className="muted">No stops in this range.</p>
            )}
          </Panel>

          <Panel
            title="DIFOT by depot / region"
            subtitle="Delivered, in full, and DIFOT by team region"
          >
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th className="num">Stops</th>
                    <th className="num">DIFOT</th>
                    <th className="num">Delivered</th>
                    <th className="num">In full</th>
                    <th className="num">Avg distance</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byRegion.map((r) => (
                    <tr key={r.region}>
                      <td>{r.region}</td>
                      <td className="num">{num(r.stops)}</td>
                      <td className="num">
                        <span className="util-bar" aria-hidden="true">
                          <span className="util-fill" style={{ width: `${Math.min(100, r.difotRate * 100)}%` }} />
                        </span>
                        {pct(r.difotRate, 1)}
                      </td>
                      <td className="num">{pct(r.deliveredRate, 1)}</td>
                      <td className="num">{pct(r.inFullRate, 1)}</td>
                      <td className="num">{km(r.avgKm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </section>
  );
}
